import React from "react";
import moment from "moment";

/**
 * "Overdue 2 weeks" pill (Task 24757).
 *
 * A due date on its own makes you do arithmetic - "20 Mar 2026" against
 * today's date, per row, down a list. The pill does the arithmetic once and
 * says the thing you actually wanted to know.
 *
 * DELIBERATELY COARSE. It rounds to days, then weeks, then months, then
 * years, because "overdue 47 days" is a number you have to think about and
 * "overdue 6 weeks" is a fact you can act on. Precision past the point of
 * decision is noise.
 *
 * Props:
 *   dueDateTime - the due date (anything moment can parse). No date, no pill.
 *   settled     - true when there is nothing left to chase. A paid invoice
 *                 that was paid late is not overdue, it is finished, and
 *                 flagging it red forever is how people learn to ignore red.
 */

// Whole calendar days between the due date and today, ignoring the time of
// day. Without startOf("day") an invoice due at 17:00 reads as "0 days" all
// the next morning and then flips to 1 in the afternoon.
const overdueDays = (dueDateTime) => {
  const due = moment(dueDateTime);
  if (!dueDateTime || !due.isValid()) return null;
  return moment().startOf("day").diff(due.startOf("day"), "days");
};

const describe = (days) => {
  if (days < 7) return `${days} day${days === 1 ? "" : "s"}`;

  const weeks = Math.floor(days / 7);
  if (days < 56) return `${weeks} week${weeks === 1 ? "" : "s"}`;

  const months = Math.floor(days / 30);
  if (days < 730) return `${months} month${months === 1 ? "" : "s"}`;

  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? "" : "s"}`;
};

// Three bands rather than a gradient - a colour you cannot name is not
// carrying information. Within terms / a few days late / seriously late.
const severity = (days) => {
  if (days >= 60) return "danger";
  if (days >= 14) return "warning";
  return "secondary";
};

export default function OverdueByPill({ dueDateTime, settled = false }) {
  if (settled) return null;

  const days = overdueDays(dueDateTime);
  if (days === null || days <= 0) return null;

  const tone = severity(days);

  return (
    <span
      className={`badge bg-soft-${tone} text-${tone} ms-1`}
      title={`Due ${moment(dueDateTime).format("D MMM YYYY")} - overdue by ${days} day${
        days === 1 ? "" : "s"
      }`}
    >
      Overdue {describe(days)}
    </span>
  );
}

/**
 * Is this document overdue? (Task 24767)
 *
 * The Overdue tabs on the invoice and proforma lists call THIS rather than
 * writing their own date comparison, so a row that carries the pill is
 * exactly a row the tab shows. Two definitions of overdue on the same screen
 * is how a list ends up disagreeing with the badges inside it.
 */
export const isOverdue = (dueDateTime, settled = false) => {
  if (settled) return false;
  const days = overdueDays(dueDateTime);
  return days !== null && days > 0;
};

// Exported for anywhere that wants the wording without the pill (a tooltip, a
// summary line), so the two can never describe the same gap differently.
export { overdueDays, describe as describeOverdue };
